import { RoleType } from '@shared/roles.core'
import { TaskMap, TaskView, isEnded } from '@client/stores/progressStore'

//
// The conversion queue as the converter list shows it.
//
// The list has a row per file of a converter's data list; the server queues
// conversions per such file (services/convert/queue, activeKeyOf). These are
// the rules that put the tasks of the progress store into those rows, kept
// apart from the view so they can be tested without one.
//

export type TQueueUser = { _id?: unknown; role?: unknown } | null | undefined

/** The row of a file: the same file under another converter is another row. */
export const rowKey = (converterId: unknown, inTableId: unknown, fileId: unknown) =>
    [converterId, inTableId, fileId].map((part) => String(part ?? '')).join('|')

const rowKeyOf = (task: TaskView) => rowKey(task.detail.converterId, task.detail.inTableId, task.detail.fileId)

/** Waits or runs: the row is locked then, and shows a symbol instead of its checkbox. */
export const isActive = (task?: TaskView | null) => !!task && !isEnded(task)

/** An ended task is shown for as long as the server keeps it. */
const isShown = (task: TaskView, now: number) =>
    isActive(task) || !task.expiresAt || Date.parse(task.expiresAt) > now

/** Task ids are mongo ids, and those begin with their time: the larger is the newer. */
const isNewer = (a: TaskView, b: TaskView) => a.id > b.id

/**
 * The task each row of the project shows: one that waits or runs if there is
 * one, otherwise the newest that ended and is still kept.
 */
export function queueByRow(tasks: TaskMap, projectId: string, now = Date.now()): Record<string, TaskView> {
    const rows: Record<string, TaskView> = {}

    for (const task of Object.values(tasks)) {
        if (task.kind !== 'convert' || task.projectId !== projectId || !isShown(task, now)) continue

        const key = rowKeyOf(task)
        const shown = rows[key]
        if (!shown || (isActive(task) && !isActive(shown)) || (isActive(task) === isActive(shown) && isNewer(task, shown))) {
            rows[key] = task
        }
    }

    return rows
}

/**
 * Whether the user may cancel this conversion: the one who started it,
 * administrators, and the owner (operator, 17.10.2026, F2).
 *
 * The server checks the owner against the project's tenant. The client knows
 * no tenant, and needs none: an owner only ever sees the projects of his own.
 * The endpoint decides in the end either way.
 */
export function mayCancel(task: TaskView, user: TQueueUser): boolean {
    if (!user || !isActive(task)) return false
    if (user.role === RoleType.Administrator || user.role === RoleType.Owner) return true

    return !!task.createdBy && task.createdBy === String(user._id ?? '')
}

/** The user's own conversions that still wait or run, among the rows. */
export const ownActive = (rows: Record<string, TaskView>, user: TQueueUser): TaskView[] =>
    Object.values(rows).filter((task) => isActive(task) && !!user && task.createdBy === String(user._id ?? ''))

/** In how many ms the next ended row goes back to a plain row, or null if none will. */
export function nextExpiry(rows: Record<string, TaskView>, now = Date.now()): number | null {
    let next: number | null = null

    for (const task of Object.values(rows)) {
        if (isActive(task) || !task.expiresAt) continue
        const left = Date.parse(task.expiresAt) - now
        if (left > 0 && (next === null || left < next)) next = left
    }

    return next
}
